import { CircularProgress, makeStyles } from '@material-ui/core';
import * as React from 'react';
import { BreadcrumbsProvider } from 'react-breadcrumbs-dynamic';
import { Redirect, Route, Switch } from 'react-router-dom';
import { NewExternalOrderAlert } from 'components';
import { UserTypes } from 'enums';
import { User } from 'interfaces';
import { UserService } from 'services';
import { TableBoard } from 'views';
import { Breadcrumbs, Header, Sidebar } from './components';
import { getRoutes } from './routes';

interface Props {
  componentError?: Error;
}

const useStyles = makeStyles((theme) => ({
  main: {
    height: '100%',
    color: theme.palette.text.primary,
    [theme.breakpoints.up('lg')]: {
      marginLeft: theme.drawerWidth,
    },
  },
  content: {
    ...theme.mixins.gutters(),
  },
  loadingWrapper: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
}));

export const AppLayout: React.FC<Props> = ({ componentError }) => {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [me, setMe] = React.useState<User | null>(null);
  const [loadingMe, setLoadingMe] = React.useState(true);

  const routes = getRoutes();

  React.useEffect(() => {
    UserService.getUserWithToken()
      .then((u) => setMe(u))
      .catch(() => setMe(null))
      .finally(() => setLoadingMe(false));
  }, []);

  if (loadingMe) {
    return (
      <div className={classes.loadingWrapper}>
        <CircularProgress />
      </div>
    );
  }

  // Waiters get ONLY the full-screen table board — no sidebar, no header, and
  // no Bolt/Wolt takeover alert (the alert component is never even mounted).
  if (me && me.userType && me.userType.id === UserTypes.WAITER) {
    return <TableBoard />;
  }

  return (
    <React.Fragment>
      {/* Global takeover alert for new Bolt/Wolt orders. Renders nothing
          visible unless the logged-in user is allocated to a shop AND a
          new order arrives — at which point it grabs the screen with a
          modal + looping audio until the user accepts or declines. */}
      <NewExternalOrderAlert />

      <Sidebar
        open={drawerOpen}
        openDrawer={openDrawer}
        closeDrawer={closeDrawer}
        routes={routes}
      />

      <main className={classes.main}>
        {routes && (
          <Switch>
            {routes.map(
              ({
                component: Component,
                render,
                headerTitle,
                ...routeProps
              }) => (
                <Route
                  {...routeProps}
                  key={routeProps.path}
                  render={(componentProps) => (
                    <React.Fragment>
                      <Header onToggleDrawer={toggleDrawer} routes={routes}>
                        {headerTitle}
                      </Header>

                      <BreadcrumbsProvider>
                        <div className={classes.content}>
                          <Breadcrumbs />

                          {Component ? (
                            <Component {...componentProps} />
                          ) : render ? (
                            render(componentProps)
                          ) : null}
                        </div>
                      </BreadcrumbsProvider>
                    </React.Fragment>
                  )}
                />
              ),
            )}

            <Route>
              <Redirect to="/dashboard" />
            </Route>
          </Switch>
        )}
      </main>
    </React.Fragment>
  );

  function toggleDrawer() {
    setDrawerOpen((open) => !open);
  }

  function openDrawer() {
    if (!drawerOpen) {
      toggleDrawer();
    }
  }

  function closeDrawer() {
    if (drawerOpen) {
      toggleDrawer();
    }
  }
};
